import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button, Label, DateInput } from '../ui';
import { XIcon, ChevronDownIcon, CalendarIcon } from '../ui/Icons';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface DateFilterOption {
  label: string;
  value: string;
  getRange: () => DateRange;
  icon?: React.ReactNode;
}

interface DateFilterDropdownProps {
  label: string;
  selectedValue: string | null;
  onFilterChange: (value: string | null, range?: DateRange) => void;
  placeholder?: string;
}

const DateFilterDropdown: React.FC<DateFilterDropdownProps> = ({
  label,
  selectedValue,
  onFilterChange,
  placeholder = 'All Dates',
}) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const now = new Date();
  // Create today as UTC date to avoid timezone issues
  const today = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate()
  ));

  const formatDate = (date: Date): string => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };

  const formatDateRange = (start: Date, end: Date): string => {
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const getDateOptions = (): DateFilterOption[] => {
    // Use UTC methods to ensure consistent date calculations regardless of timezone
    const todayUTC = new Date(Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate()
    ));
    
    const yesterday = new Date(todayUTC);
    yesterday.setUTCDate(yesterday.getUTCDate() - 1);

    const last7Days = new Date(todayUTC);
    last7Days.setUTCDate(last7Days.getUTCDate() - 7);

    const lastMonth = new Date(todayUTC);
    lastMonth.setUTCMonth(lastMonth.getUTCMonth() - 1);

    const last3Months = new Date(todayUTC);
    last3Months.setUTCMonth(last3Months.getUTCMonth() - 3);

    const last6Months = new Date(todayUTC);
    last6Months.setUTCMonth(last6Months.getUTCMonth() - 6);

    const lastYear = new Date(todayUTC);
    lastYear.setUTCFullYear(lastYear.getUTCFullYear() - 1);

    const thisWeekStart = new Date(todayUTC);
    const dayOfWeek = today.getUTCDay(); // 0 = Sunday, 1 = Monday, etc.
    thisWeekStart.setUTCDate(todayUTC.getUTCDate() - dayOfWeek);
    const thisWeekEnd = new Date(thisWeekStart);
    thisWeekEnd.setUTCDate(thisWeekStart.getUTCDate() + 6);

    const thisMonthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
    const thisMonthEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0));

    const thisYearStart = new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
    const thisYearEnd = new Date(Date.UTC(today.getUTCFullYear(), 11, 31));

    // Next week
    const nextWeekStart = new Date(thisWeekStart);
    nextWeekStart.setUTCDate(thisWeekStart.getUTCDate() + 7);
    const nextWeekEnd = new Date(nextWeekStart);
    nextWeekEnd.setUTCDate(nextWeekStart.getUTCDate() + 6);

    // Next 7 / 15 / 30 days (from tomorrow through N days ahead)
    const tomorrow = new Date(todayUTC);
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    const next7DaysEnd = new Date(todayUTC);
    next7DaysEnd.setUTCDate(next7DaysEnd.getUTCDate() + 7);
    const next15DaysEnd = new Date(todayUTC);
    next15DaysEnd.setUTCDate(next15DaysEnd.getUTCDate() + 15);
    const next30DaysEnd = new Date(todayUTC);
    next30DaysEnd.setUTCDate(next30DaysEnd.getUTCDate() + 30);
    const next90DaysEnd = new Date(todayUTC);
    next90DaysEnd.setUTCDate(next90DaysEnd.getUTCDate() + 90);

    // Next month
    const nextMonthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1));
    const nextMonthEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 2, 0));

    // Next 3 months
    const next3MonthsStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1));
    const next3MonthsEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 4, 0));

    // Next 6 months
    const next6MonthsStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1));
    const next6MonthsEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 7, 0));

    // Next year
    const nextYearStart = new Date(Date.UTC(today.getUTCFullYear() + 1, 0, 1));
    const nextYearEnd = new Date(Date.UTC(today.getUTCFullYear() + 1, 11, 31));

    return [
      {
        label: 'All Dates',
        value: 'all',
        getRange: () => ({ start: null, end: null }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'Today',
        value: 'today',
        getRange: () => {
          // Create UTC dates for today to avoid timezone issues
          const todayStart = new Date(Date.UTC(
            today.getUTCFullYear(),
            today.getUTCMonth(),
            today.getUTCDate()
          ));
          return { start: todayStart, end: todayStart };
        },
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
        ),
      },
      {
        label: 'Overdue',
        value: 'overdue',
        getRange: () => {
          // Return dates before today (overdue items)
          // Calculate yesterday relative to current date to ensure it's always up-to-date
          const now = new Date();
          const currentToday = new Date(Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate()
          ));
          // Set end to yesterday at end of day (23:59:59.999) to exclude today
          const currentYesterday = new Date(Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate() - 1,
            23, 59, 59, 999
          ));
          // Use a very early date (epoch start) for start to include all past dates
          // This ensures modules that require both start and end will work correctly
          const epochStart = new Date(Date.UTC(1970, 0, 1, 0, 0, 0, 0));
          return { start: epochStart, end: currentYesterday };
        },
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        ),
      },
      {
        label: 'Yesterday',
        value: 'yesterday',
        getRange: () => ({ start: yesterday, end: yesterday }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'Last 7 Days',
        value: 'last7days',
        getRange: () => ({ start: last7Days, end: todayUTC }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'Last Month',
        value: 'lastMonth',
        getRange: () => ({ start: lastMonth, end: todayUTC }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'Last 3 Months',
        value: 'last3Months',
        getRange: () => ({ start: last3Months, end: todayUTC }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
      },
      {
        label: 'Last 6 Months',
        value: 'last6Months',
        getRange: () => ({ start: last6Months, end: todayUTC }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
          </svg>
        ),
      },
      {
        label: 'Last Year',
        value: 'lastYear',
        getRange: () => ({ start: lastYear, end: todayUTC }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'This Week',
        value: 'thisWeek',
        getRange: () => ({ start: thisWeekStart, end: todayUTC }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'This Month',
        value: 'thisMonth',
        getRange: () => ({ start: thisMonthStart, end: todayUTC }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'This Year',
        value: 'thisYear',
        getRange: () => ({ start: thisYearStart, end: todayUTC }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'Current Week',
        value: 'currentWeek',
        getRange: () => ({ start: thisWeekStart, end: thisWeekEnd }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'Current Month',
        value: 'currentMonth',
        getRange: () => ({ start: thisMonthStart, end: thisMonthEnd }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'Current Year',
        value: 'currentYear',
        getRange: () => ({ start: thisYearStart, end: thisYearEnd }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'Next Week',
        value: 'nextWeek',
        getRange: () => ({ start: nextWeekStart, end: nextWeekEnd }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        ),
      },
      {
        label: 'Next 7 Days',
        value: 'next7days',
        getRange: () => ({ start: tomorrow, end: next7DaysEnd }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'Next 15 Days',
        value: 'next15days',
        getRange: () => ({ start: tomorrow, end: next15DaysEnd }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'Next 30 Days',
        value: 'next30days',
        getRange: () => ({ start: tomorrow, end: next30DaysEnd }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'Next 90 Days',
        value: 'next90days',
        getRange: () => ({ start: tomorrow, end: next90DaysEnd }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      },
      {
        label: 'Next Month',
        value: 'nextMonth',
        getRange: () => ({ start: nextMonthStart, end: nextMonthEnd }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        ),
      },
      {
        label: 'Next 3 Months',
        value: 'next3Months',
        getRange: () => ({ start: next3MonthsStart, end: next3MonthsEnd }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        ),
      },
      {
        label: 'Next 6 Months',
        value: 'next6Months',
        getRange: () => ({ start: next6MonthsStart, end: next6MonthsEnd }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        ),
      },
      {
        label: 'Next Year',
        value: 'nextYear',
        getRange: () => ({ start: nextYearStart, end: nextYearEnd }),
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        ),
      },
    ];
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dateOptions = useMemo(() => getDateOptions(), []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCustomRange(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionSelect = (option: DateFilterOption) => {
    if (option.value === 'custom') {
      setShowCustomRange(true);
    } else {
      const range = option.getRange();
      onFilterChange(option.value, range);
      setIsOpen(false);
    }
  };

  const handleCustomRangeApply = () => {
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      onFilterChange('custom', { start, end });
      setIsOpen(false);
      setShowCustomRange(false);
    }
  };

  const handleClear = () => {
    onFilterChange(null);
    setCustomStartDate('');
    setCustomEndDate('');
    setShowCustomRange(false);
  };

  const formatDateForInput = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const displayText = useMemo(() => {
    if (!selectedValue || selectedValue === 'all') {
      return placeholder;
    }
    const option = dateOptions.find((opt) => opt.value === selectedValue);
    if (option) {
      if (selectedValue === 'custom') {
        if (customStartDate && customEndDate) {
          return `${formatDateForInput(new Date(customStartDate))} - ${formatDateForInput(new Date(customEndDate))}`;
        }
        return 'Custom Range';
      }
      return option.label;
    }
    return placeholder;
  }, [selectedValue, dateOptions, customStartDate, customEndDate, placeholder]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Label className="text-xs mb-2">{label}</Label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full px-3 py-2 text-sm text-left ${colors.surface} border ${colors.border} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 flex items-center justify-between`}
        >
          <div className="flex items-center space-x-2">
            <CalendarIcon className="w-4 h-4 text-indigo-600" />
            <span className={!selectedValue || selectedValue === 'all' ? colors.textMuted : colors.text}>
              {displayText}
            </span>
          </div>
          <ChevronDownIcon className={`w-4 h-4 ${colors.textMuted} transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className={`absolute z-50 ${showCustomRange ? 'w-[340px]' : 'w-full'} mt-1 bg-white border border-gray-300 rounded-lg shadow-lg ${showCustomRange ? 'max-h-[500px]' : 'max-h-96'} overflow-hidden`}>
            {!showCustomRange ? (
              <>
                {/* Date Range Header */}
                <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Date Range</span>
                  </div>
                </div>

                {/* Options List */}
                <div className="max-h-80 overflow-y-auto">
                  {dateOptions.map((option) => {
                    const isSelected = selectedValue === option.value;
                    const range = option.getRange();
                    const rangeText = range.start && range.end ? formatDateRange(range.start, range.end) : '';

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleOptionSelect(option)}
                        className={`w-full flex items-center justify-between px-3 py-2 hover:${colors.surfaceHover} cursor-pointer ${
                          isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        <div className="flex items-center space-x-2 flex-1">
                          <div className="text-indigo-600">{option.icon}</div>
                          <div className="flex flex-col items-start">
                            <span className={`text-sm ${colors.text}`}>{option.label}</span>
                            {rangeText && (
                              <span className={`text-xs ${colors.textMuted}`}>{rangeText}</span>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    );
                  })}

                  {/* Custom Range Option */}
                  <button
                    type="button"
                    onClick={() => setShowCustomRange(true)}
                    className={`w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 cursor-pointer ${
                      selectedValue === 'custom' ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-2 flex-1">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c1.162-.5 2.385-.5 3.35 0a5.5 5.5 0 011.413 1.413c.5 1.162.5 2.385 0 3.35a5.5 5.5 0 01-1.413 1.413c-1.162.5-2.385.5-3.35 0a5.5 5.5 0 01-1.413-1.413c-.5-1.162-.5-2.385 0-3.35a5.5 5.5 0 011.413-1.413z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-sm text-gray-900">Custom Range</span>
                    </div>
                    {selectedValue === 'custom' && (
                      <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
              </>
            ) : (
              /* Custom Range Panel */
              <div className="p-4 w-full">
                {/* Header */}
                <div className={`flex items-center justify-between mb-3 pb-2 border-b ${colors.border}`}>
                  <h3 className={`text-sm font-semibold ${colors.text}`}>{label}</h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setShowCustomRange(false);
                      if (selectedValue !== 'custom') {
                        handleClear();
                      }
                    }}
                    title="Close"
                  >
                    <XIcon className="w-5 h-5" />
                  </Button>
                </div>

                {/* Custom Range Label */}
                <div className="mb-4 p-2.5 bg-indigo-50 border border-indigo-100 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c1.162-.5 2.385-.5 3.35 0a5.5 5.5 0 011.413 1.413c.5 1.162.5 2.385 0 3.35a5.5 5.5 0 01-1.413 1.413c-1.162.5-2.385.5-3.35 0a5.5 5.5 0 01-1.413-1.413c-.5-1.162-.5-2.385 0-3.35a5.5 5.5 0 011.413-1.413z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-sm font-medium text-indigo-900">Custom Range</span>
                  </div>
                </div>

                {/* Date Inputs */}
                <div className="space-y-3 mb-4">
                  <div>
                    <Label className="text-xs mb-2">Start Date</Label>
                    <DateInput
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value || '')}
                      placeholder="Select start date"
                      showClearButton={false}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs mb-2">End Date</Label>
                    <DateInput
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value || '')}
                      placeholder="Select end date"
                      showClearButton={false}
                      minDate={customStartDate || undefined}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-2 pt-2 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClear}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleCustomRangeApply}
                    disabled={!customStartDate || !customEndDate}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DateFilterDropdown;

